import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Typography } from '../../interfaces/typography';
import { getServicesManager } from '../../services';
import { RootState } from '../../store';

interface SalesforceCallbackProps {
  apiKey?: string;
}

type CallbackStatus = 'processing' | 'success' | 'error';

export function SalesforceCallback({ apiKey: apiKeyProp }: SalesforceCallbackProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [message, setMessage] = useState<string>('');
  const [instanceUrl, setInstanceUrl] = useState<string>('');
  const hasRun = useRef(false);

  // Get apiKey from Redux state if not provided as prop
  const apiKeyFromState = useSelector((state: RootState) => state.userAuth.value?.api_key);
  const apiKey = apiKeyProp || apiKeyFromState || '';

  const services = getServicesManager();

  useEffect(() => {
    // Prevent double execution in React StrictMode (development)
    if (hasRun.current) {
      return;
    }
    hasRun.current = true;

    handleCallback();
  }, []);

  const handleCallback = async () => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    // Check for OAuth errors
    if (error) {
      setStatus('error');
      setMessage(`Salesforce authorization failed: ${errorDescription || error}`);
      return;
    }

    // Check for authorization code
    if (!code) {
      setStatus('error');
      setMessage('No authorization code received from Salesforce');
      return;
    }

    try {
      // Exchange authorization code for tokens
      const redirectUri = `${window.location.origin}/settings/integrations/salesforce/callback`;

      const data = await services.postSalesforceOAuthCallback(
        code,
        state || '',
        redirectUri
      );

      if (!data || !data.success) {
        throw new Error('Failed to complete authorization');
      }

      // Success!
      setStatus('success');
      setInstanceUrl(data.instance_url || '');
      setMessage('Successfully connected to Salesforce!');

      // Redirect to integrations page after 2 seconds
      setTimeout(() => {
        navigate('/settings?tab=Integrations');
      }, 2000);
    } catch (error) {
      console.error('Error in Salesforce callback:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="max-w-md w-full p-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
              <img
                src="/assets/salesforce.svg"
                alt="Salesforce"
                className="w-10 h-10 object-contain"
              />
            </div>
          </div>

          {/* Processing */}
          {status === 'processing' && (
            <div className="flex flex-col items-center gap-4">
              <svg className="animate-spin h-12 w-12 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div className="text-center">
                <Typography variant="h4" color="#1F2937" fontWeight={600}>
                  Connecting to Salesforce
                </Typography>
                <Typography variant="mediumParagraph" color="#6B7280" className="mt-2">
                  Please wait while we complete the authorization...
                </Typography>
              </div>
            </div>
          )}

          {/* Success */}
          {status === 'success' && (
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-center">
                <Typography variant="h4" color="#059669" fontWeight={600}>
                  Successfully Connected!
                </Typography>
                <Typography variant="mediumParagraph" color="#047857" className="mt-2">
                  {message}
                </Typography>
                {instanceUrl && (
                  <Typography variant="smallParagraph" color="#6B7280" className="mt-3">
                    Connected to: {instanceUrl}
                  </Typography>
                )}
                <div className="mt-6 flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Redirecting to settings...
                  </Typography>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="text-center">
                <Typography variant="h4" color="#DC2626" fontWeight={600}>
                  Connection Failed
                </Typography>
                <Typography variant="mediumParagraph" color="#7F1D1D" className="mt-2">
                  {message}
                </Typography>
                <button
                  onClick={() => navigate('/settings?tab=Integrations')}
                  className="mt-6 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                >
                  <Typography variant="mediumParagraph" color="#FFFFFF" fontWeight={600}>
                    Back to Settings
                  </Typography>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <Typography variant="smallParagraph" color="#9CA3AF">
            Need help? Contact support at{' '}
            <a href="mailto:support@trellus.ai" className="text-blue-600 hover:text-blue-700">
              support@trellus.ai
            </a>
          </Typography>
        </div>
      </div>
    </div>
  );
}
